// Visit (level-of-visit) rows in the dashboard card's Orders / Tasks box.
// Owner 09-29: "We should always show visit order with lov (AI) or (provider)
// if it is there." Appointment 655339 had lov 5 but no orders_master rows, so
// the card said "No orders yet" while the chart showed 99215.
//
// Besides the real VISIT orders, POST /appointments/dashboard sends a
// display-only VISIT row when the appointment has a level of visit and no
// VISIT order:
//   { id: null, ordertype: "VISIT", status: "CREATED", tov: "EST - Level 5",
//     lov, synthetic: true, lov_source: "AI" | "provider" | null }
// and lov_source on real VISIT rows too. A synthetic row is not an
// orders_master row: nothing may complete, cancel, schedule, hide or annotate
// it, and it never holds up checkout (see visitflow.js).
// Pure functions (no React). Test:
//   node --no-warnings src/landingscreen/cards/visitrow.test.mjs

const CLOSED_STATUSES = ["COMPLETED", "CANCELLEDTASK"];

export const isVisitOrder = (o) =>
  String(o?.ordertype || "").trim().toUpperCase() === "VISIT";

// The dashboard's display-only visit row (synthetic: true).
export const isSyntheticOrder = (o) => {
  const s = o?.synthetic;
  return s === true || s === 1 || ["true", "1"].includes(String(s ?? "").trim().toLowerCase());
};

const hasOrderId = (o) =>
  o?.id !== null && o?.id !== undefined && String(o.id).trim() !== "";

// Rows an action (complete, cancel, schedule, hide, notes, result) may be
// sent for: real orders_master rows only.
export const isActionableOrder = (o) =>
  Boolean(o) && !isSyntheticOrder(o) && hasOrderId(o);

// Who set the level of visit: the AI note (BlinkScribe) or the provider (chart
// level-of-visit selector). Unknown (older rows): no tag.
export const lovSourceTag = (o) => {
  const s = String(o?.lov_source || "").trim().toLowerCase();
  if (s === "ai")
    return { kind: "ai", text: "AI", title: "Level of visit from the AI note" };
  if (s === "provider")
    return {
      kind: "provider",
      text: "Provider",
      title: "Level of visit set by the provider",
    };
  return null;
};

// The Visit line(s) of a VISIT order: the level (tov, with its source tag) and
// the secondary wellness slot (tov2). A real row with no labels shows nothing,
// as before; a synthetic row always shows its level.
export const visitRowValues = (o) => {
  const out = [];
  const tov = String(o?.tov || "").trim();
  const lov = Number(o?.lov);
  if (tov) out.push({ slot: "tov", value: tov, source: lovSourceTag(o) });
  else if (isSyntheticOrder(o) && lov > 0)
    out.push({ slot: "tov", value: `Level ${lov}`, source: lovSourceTag(o) });
  const tov2 = String(o?.tov2 || "").trim();
  if (tov2) out.push({ slot: "tov2", value: tov2, source: null });
  return out;
};

const isClosed = (o) =>
  CLOSED_STATUSES.includes(String(o?.status ?? "").trim().toUpperCase());

// Listed rows: one per order, a VISIT order one per Visit line.
export const orderRowCount = (list) =>
  (Array.isArray(list) ? list : []).reduce(
    (acc, o) => acc + (isVisitOrder(o) ? visitRowValues(o).length : 1),
    0,
  );

// The Orders / Tasks badge. While a real order is open it counts the open rows
// (a synthetic visit row included, as a real open VISIT order is); once every
// real order is completed/cancelled, or the box holds only display-only visit
// rows, it counts every row and greys out (allInactive), since nobody can
// close a synthetic row.
export const orderBadge = (orders) => {
  const list = Array.isArray(orders) ? orders : [];
  const allInactive =
    list.length > 0 && list.filter((o) => !isSyntheticOrder(o)).every(isClosed);
  const count = allInactive
    ? orderRowCount(list)
    : orderRowCount(list.filter((o) => !isClosed(o)));
  return { count, allInactive };
};
